import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, courseImageUrl, formatDate, type Diplomado, type DiplomadoEdition, type Enrollment } from "@/lib/api";
import { EditarDiplomadoEdicionModal, EditarDiplomadoModal, NuevaDiplomadoEdicionModal } from "@/components/feature/NuevoDiplomadoModal";
import ManualEnrollModal from "@/components/feature/ManualEnrollModal";

export default function DiplomadoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [diplomado, setDiplomado] = useState<Diplomado | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [editionModalOpen, setEditionModalOpen] = useState(false);
  const [editDiplomadoOpen, setEditDiplomadoOpen] = useState(false);
  const [editEdition, setEditEdition] = useState<DiplomadoEdition | null>(null);
  const [togglingLanding, setTogglingLanding] = useState(false);
  const [enrollModalEdition, setEnrollModalEdition] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [diplomadoData, enrollmentsData] = await Promise.all([
        api.getDiplomado(id),
        api.getEnrollments(),
      ]);
      setDiplomado(diplomadoData);
      setEnrollments(enrollmentsData.filter((e) => e.diplomado_id === id));
    } catch {
      setDiplomado(null);
      setEnrollments([]);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleLanding = async () => {
    if (!diplomado) return;
    setTogglingLanding(true);
    try {
      setDiplomado(await api.updateDiplomado(diplomado.id, { show_on_landing: !diplomado.show_on_landing }));
    } catch {
      /* ignore */
    }
    setTogglingLanding(false);
  };

  const handleDeleteDiplomado = async () => {
    if (!diplomado) return;
    const editionCount = diplomado.editions?.length || 0;
    const message = editionCount === 0
      ? "¿Seguro que deseas ELIMINAR COMPLETAMENTE este diplomado del sistema? Esta acción no se puede deshacer."
      : "¿Seguro que deseas archivar este diplomado? Se ocultará pero mantendrá el historial de sus ediciones.";
    if (!confirm(message)) return;
    await api.deleteDiplomado(diplomado.id);
    navigate("/diplomados");
  };

  const editionStatusBadge = (status: string) => {
    switch (status) {
      case "programado":
        return <span className="badge-info">Programado</span>;
      case "en_curso":
        return <span className="badge-accent">En Curso</span>;
      case "completado":
        return <span className="badge-success">Completado</span>;
      case "cancelado":
        return <span className="badge-danger">Cancelado</span>;
      default:
        return <span className="badge-info">{status}</span>;
    }
  };

  const getEditionEnrollments = (editionId: string) =>
    enrollments.filter((e) => e.diplomado_edition_id === editionId);

  const attLabel = (status: string) => {
    switch (status) {
      case "presente":
        return <span className="badge-success">Presente</span>;
      case "ausente":
        return <span className="badge-danger">Ausente</span>;
      default:
        return <span className="badge-info">Pendiente</span>;
    }
  };

  const evalLabel = (status: string) => {
    switch (status) {
      case "aprobado":
        return <span className="badge-success">Aprobado</span>;
      case "rechazado":
        return <span className="badge-danger">Rechazado</span>;
      case "revision":
        return <span className="badge-warning">Revisión</span>;
      default:
        return <span className="badge-info">Pendiente</span>;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
        <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando diplomado...</p>
      </div>
    );
  }

  if (!diplomado) {
    return (
      <div className="text-center py-20">
        <i className="ri-graduation-cap-line text-4xl text-[var(--color-text-muted)]"></i>
        <p className="text-[var(--color-text-muted)] mt-3">Diplomado no encontrado</p>
        <button onClick={() => navigate("/diplomados")} className="btn-primary mt-4 cursor-pointer">Volver a diplomados</button>
      </div>
    );
  }

  const editions = diplomado.editions ?? [];
  const attCount = enrollments.filter((e) => e.attendance_status === "presente").length;
  const absCount = enrollments.filter((e) => e.attendance_status === "ausente").length;
  const certCount = enrollments.filter((e) => e.certificate_issued).length;
  const paidCount = enrollments.filter((e) => e.payment_status === "pagado" || e.payment_status === "cortesia").length;
  const evalPending = enrollments.filter((e) => e.attendance_status === "presente" && e.evaluation_status === "pendiente").length;

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/diplomados")} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer">
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <div className="flex-1 flex items-start gap-4">
          <img src={courseImageUrl(diplomado.image_url)} alt={diplomado.name} className="w-16 h-16 rounded-xl object-cover border border-[var(--color-border)] flex-shrink-0" />
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{diplomado.name}</h1>
              {diplomado.active ? <span className="badge-success">Activo</span> : <span className="badge-danger">Inactivo</span>}
              <span className="badge-info">{diplomado.category}</span>
            </div>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">{diplomado.description}</p>
          </div>
        </div>
        <button onClick={() => setEditDiplomadoOpen(true)} className="btn-secondary flex items-center gap-2 cursor-pointer">
          <i className="ri-edit-line"></i>
          Editar
        </button>
        <button onClick={handleDeleteDiplomado} className="px-4 py-2 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500/20 transition-colors flex items-center gap-2 cursor-pointer font-medium text-sm">
          <i className="ri-delete-bin-line"></i>
          Eliminar
        </button>
        <button onClick={() => setEditionModalOpen(true)} className="btn-primary flex items-center gap-2 cursor-pointer">
          <i className="ri-add-line"></i>
          Nueva Edición
        </button>
      </div>

      <div className="card flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
          <input type="checkbox" checked={diplomado.show_on_landing} disabled={togglingLanding} onChange={handleToggleLanding} className="cursor-pointer" />
          Mostrar en landing page
        </label>
        <span className="text-xs text-[var(--color-text-muted)]">{editions.length} edición(es) · {diplomado.duration} · {diplomado.hours}h</span>
      </div>

      <div className="card">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Contenido público del detalle</h2>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">Información visible en la página pública del diplomado.</p>
          </div>
          <button onClick={() => setEditDiplomadoOpen(true)} className="text-xs text-[var(--color-text-accent)] hover:underline cursor-pointer">Editar contenido</button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Título público</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1 line-clamp-2">{diplomado.detail_title || diplomado.name}</p>
          </div>
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Modalidad</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1">{diplomado.modality || "Por definir"}</p>
          </div>
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Destacados</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1">{diplomado.highlights?.length || 0} punto(s)</p>
          </div>
          <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Recursos</span>
            <p className="font-medium text-[var(--color-text-primary)] mt-1">{diplomado.resource_files?.length || 0} archivo(s)</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-[var(--color-text-primary)]">{enrollments.length}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Inscritos</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-[var(--color-text-accent)]">{attCount}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Presentes</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-red-400">{absCount}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Ausentes</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-yellow-400">{evalPending}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Pend. Evaluar</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-green-400">{certCount}</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Certificados</p>
        </div>
        <div className="card text-center py-4 px-3">
          <p className="text-xl font-bold text-[var(--color-text-primary)]">{Math.round((paidCount / Math.max(enrollments.length, 1)) * 100)}%</p>
          <p className="text-[11px] text-[var(--color-text-muted)]">Liquidados</p>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Ediciones del diplomado</h2>
        {editions.length === 0 ? (
          <div className="card text-center py-12">
            <i className="ri-calendar-line text-3xl text-[var(--color-text-muted)]"></i>
            <p className="text-sm text-[var(--color-text-muted)] mt-2">Sin ediciones registradas</p>
            <button onClick={() => setEditionModalOpen(true)} className="btn-primary mt-4 cursor-pointer">Crear primera edición</button>
          </div>
        ) : (
          editions.map((edition) => {
            const editionEnrs = getEditionEnrollments(edition.id);
            return (
            <div key={edition.id} className="card overflow-hidden p-0">
              <div className="px-6 py-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{edition.edition_label || `${formatDate(edition.start_date)} - ${formatDate(edition.end_date)}`}</h3>
                    {editionStatusBadge(edition.status)}
                    <span className="text-xs text-[var(--color-text-muted)]">{editionEnrs.length}/{edition.capacity} inscritos</span>
                  </div>
                  <div className="grid sm:grid-cols-4 gap-x-6 gap-y-2 mt-3 text-xs text-[var(--color-text-muted)]">
                    <span><strong className="text-[var(--color-text-secondary)]">Fechas:</strong> {formatDate(edition.start_date)} - {formatDate(edition.end_date)}</span>
                    <span><strong className="text-[var(--color-text-secondary)]">Sede:</strong> {edition.location}</span>
                    <span><strong className="text-[var(--color-text-secondary)]">Precio:</strong> ${edition.price.toLocaleString("en-US")} USD</span>
                    <span><strong className="text-[var(--color-text-secondary)]">Capacidad:</strong> {edition.capacity}</span>
                  </div>
                  <p className="text-xs text-[var(--color-text-muted)] mt-2">{edition.instructor?.full_name || "Sin instructor"} · {edition.evaluator?.full_name || "Sin evaluador"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => setEnrollModalEdition(edition.id)} className="text-xs text-[var(--color-text-accent)] hover:underline cursor-pointer">Inscribir estudiante</button>
                  <button onClick={() => setEditEdition(edition)} className="text-xs text-[var(--color-text-accent)] hover:underline cursor-pointer">Editar</button>
                  {new Date(edition.start_date).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0) && (
                    <button
                      onClick={async () => {
                        if (!confirm("¿Estás seguro de eliminar esta edición?")) return;
                        await api.deleteDiplomadoEdition(edition.id);
                        loadData();
                      }}
                      className="text-xs text-red-400 hover:text-red-300 hover:underline cursor-pointer"
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
              </div>

              {editionEnrs.length === 0 ? (
                <div className="border-t border-[var(--color-border)] py-8 text-center text-sm text-[var(--color-text-muted)]">
                  Sin estudiantes inscritos en esta edición
                </div>
              ) : (
                <div className="border-t border-[var(--color-border)] overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[var(--color-border)]">
                        <th className="table-header">Estudiante</th>
                        <th className="table-header">Empresa</th>
                        <th className="table-header">Asistencia</th>
                        <th className="table-header">Evaluación</th>
                        <th className="table-header">Pago</th>
                        <th className="table-header">Certificado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editionEnrs.map((enr) => (
                        <tr key={enr.id} className="hover:bg-[var(--color-bg-card-hover)] transition-colors cursor-pointer" onClick={() => navigate(`/estudiantes/${enr.student_id}`)}>
                          <td className="table-cell">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-lg bg-[var(--color-accent)]/15 flex items-center justify-center flex-shrink-0">
                                <span className="text-[var(--color-text-accent)] text-[10px] font-semibold">{(enr.student?.full_name || "?").charAt(0)}</span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-[var(--color-text-primary)]">{enr.student?.full_name || "Desconocido"}</p>
                                <p className="text-xs text-[var(--color-text-muted)]">{enr.student?.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="table-cell"><span className="text-sm text-[var(--color-text-secondary)]">{enr.student?.company || "—"}</span></td>
                          <td className="table-cell">{attLabel(enr.attendance_status)}</td>
                          <td className="table-cell">{evalLabel(enr.evaluation_status)}</td>
                          <td className="table-cell">
                            {enr.payment_status === "pagado" ? <span className="badge-success">Pagado</span> : enr.payment_status === "cortesia" ? <span className="badge-info">Cortesía</span> : enr.payment_status === "abono_parcial" ? <span className="badge-warning">Parcial</span> : <span className="badge-danger">Pendiente</span>}
                          </td>
                          <td className="table-cell">{enr.certificate_issued ? <span className="badge-success">Emitido</span> : <span className="text-xs text-[var(--color-text-muted)]">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );})
        )}
      </div>

      <NuevaDiplomadoEdicionModal isOpen={editionModalOpen} onClose={() => setEditionModalOpen(false)} diplomadoId={diplomado.id} onCreated={loadData} />
      <EditarDiplomadoModal isOpen={editDiplomadoOpen} onClose={() => setEditDiplomadoOpen(false)} diplomado={diplomado} onSaved={loadData} />
      {editEdition && <EditarDiplomadoEdicionModal isOpen onClose={() => setEditEdition(null)} edition={editEdition} onSaved={loadData} />}
      {enrollModalEdition && (
        <ManualEnrollModal
          isOpen
          onClose={() => setEnrollModalEdition(null)}
          editionId={enrollModalEdition}
          serviceType="diplomado"
          onSuccess={loadData}
        />
      )}
    </div>
  );
}
